import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Loader } from "@mantine/core";
import type { User } from "firebase/auth";
import { getCurrentUser, signIn, signOut } from "@/utils/firebase";
import { validateRedirectUri } from "@/utils/redirects";

const USER_INSIGHTS_BASE_URL =
  import.meta.env.VITE_USER_INSIGHTS_BASE_URL ||
  "https://consulate-prod.augmoto.com";

type Stage =
  | { kind: "checking" }
  | { kind: "signed-out" }
  | { kind: "minting" }
  | { kind: "no-access"; email: string }
  | { kind: "bad-redirect" }
  | { kind: "error"; message: string };

export const Login: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirect = validateRedirectUri(params.get("redirect_uri"));
  const isLogout = params.get("logout") === "1";
  const [stage, setStage] = useState<Stage>(
    redirect ? { kind: "checking" } : { kind: "bad-redirect" },
  );
  const startedRef = useRef(false);

  const mint = useCallback(
    async (user: User) => {
      if (!redirect) return;
      setStage({ kind: "minting" });
      try {
        const idToken = await user.getIdToken();
        const response = await fetch(
          `${USER_INSIGHTS_BASE_URL}/v0/auth/token`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id_token: idToken,
              redirect_uri: redirect.href,
            }),
          },
        );

        if (response.status === 403) {
          setStage({ kind: "no-access", email: user.email ?? "(unknown)" });
          return;
        }
        if (!response.ok) {
          setStage({
            kind: "error",
            message: `Sign-in service returned ${response.status}. Please try again.`,
          });
          return;
        }

        const { custom_token } = await response.json();
        // Token travels in the URL fragment only — fragments are never sent to
        // servers, so it can't land in access logs. The receiving app strips it
        // from history before signing in (packages/auth consumeSsoToken).
        const target = redirect.href.split("#")[0];
        window.location.replace(
          `${target}#sso_token=${encodeURIComponent(custom_token)}`,
        );
      } catch {
        setStage({
          kind: "error",
          message: "Could not reach the sign-in service. Please try again.",
        });
      }
    },
    [redirect],
  );

  useEffect(() => {
    if (!redirect || startedRef.current) return;
    startedRef.current = true;

    // Explicit sign-out: clear the session here too so the user isn't
    // immediately auto-signed back in by the fast path below.
    if (isLogout) {
      signOut().finally(() => setStage({ kind: "signed-out" }));
      return;
    }

    // Fast path: already signed in here (e.g. arriving from a second app) —
    // mint straight away, no popup needed.
    getCurrentUser().then((user) => {
      if (user) {
        mint(user);
      } else {
        setStage({ kind: "signed-out" });
      }
    });
  }, [redirect, mint, isLogout]);

  const handleSignIn = async () => {
    try {
      const credential = await signIn();
      await mint(credential.user);
    } catch (error) {
      console.error("Sign in failed:", error);
      setStage({
        kind: "error",
        message: "Google sign-in was cancelled or failed.",
      });
    }
  };

  const handleSwitchAccount = async () => {
    await signOut();
    setStage({ kind: "signed-out" });
  };

  return (
    <div className="flex flex-col h-screen items-center justify-start gap-5">
      <div className="h-1/5"></div>
      <h1 className="text-2xl font-bold text-gray-900">Augmodo Sign In</h1>

      {stage.kind === "bad-redirect" && (
        <p className="text-gray-700 max-w-md text-center">
          This sign-in link is missing or has an unrecognized return address. Go
          back to the tool you were using and try again.
        </p>
      )}

      {(stage.kind === "checking" || stage.kind === "minting") && (
        <>
          <Loader />
          <p className="text-gray-500">Signing you in…</p>
        </>
      )}

      {stage.kind === "signed-out" && (
        <Button onClick={handleSignIn} size="lg">
          Sign In with Google
        </Button>
      )}

      {stage.kind === "no-access" && (
        <div className="flex flex-col items-center gap-3 max-w-md text-center">
          <p className="text-gray-700">
            <span className="font-semibold">{stage.email}</span> doesn&apos;t
            have access to Augmodo internal tools yet. Ask your lead to grant
            access in the User Insights admin.
          </p>
          <Button variant="default" onClick={handleSwitchAccount}>
            Try a different account
          </Button>
        </div>
      )}

      {stage.kind === "error" && (
        <div className="flex flex-col items-center gap-3 max-w-md text-center">
          <p className="text-red-700">{stage.message}</p>
          <Button onClick={() => setStage({ kind: "signed-out" })}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
};
